import React from 'react';
import { Trash2, ArrowRight } from 'lucide-react';
import { authAxios } from '@/lib/api';

export const EMPTY_REDIRECT = { from_path: '', to_path: '', type: 301 };
const INPUT_CLS = 'w-full border border-earth/15 rounded px-3 py-2 text-sm font-sans focus:outline-none focus:ring-2 focus:ring-saffron/30';

function AddRedirectForm({ draft, setDraft, onAdd }) {
  return (
    <div className="bg-white rounded-lg border border-saffron-100/50 p-4 mb-4">
      <p className="text-[10px] uppercase tracking-wider text-earth/40 font-sans font-semibold mb-3">Add Redirect</p>
      <div className="flex gap-2 items-end">
        <div className="flex-1">
          <label className="text-[9px] text-earth/40 font-sans">From Path</label>
          <input value={draft.from_path} onChange={e => setDraft(r => ({ ...r, from_path: e.target.value }))} placeholder="/old-page" className={INPUT_CLS} data-testid="redirect-from" />
        </div>
        <div className="flex-1">
          <label className="text-[9px] text-earth/40 font-sans">To Path</label>
          <input value={draft.to_path} onChange={e => setDraft(r => ({ ...r, to_path: e.target.value }))} placeholder="/new-page" className={INPUT_CLS} data-testid="redirect-to" />
        </div>
        <select value={draft.type} onChange={e => setDraft(r => ({ ...r, type: parseInt(e.target.value, 10) }))} className="border border-earth/15 rounded px-2 py-2 text-sm font-sans" data-testid="redirect-type">
          <option value={301}>301</option>
          <option value={302}>302</option>
        </select>
        <button onClick={onAdd} className="bg-saffron text-white font-sans font-semibold text-xs px-4 py-2.5 rounded-md hover:bg-saffron-dark" data-testid="add-redirect">Add</button>
      </div>
    </div>
  );
}

function RedirectRow({ redirect: r, onDelete }) {
  return (
    <div className="bg-white rounded-lg border border-saffron-100/50 p-3 flex items-center gap-3" data-testid={`redirect-${r.id}`}>
      <span className={`text-[10px] font-sans font-bold px-2 py-0.5 rounded ${r.type === 301 ? 'bg-green-100 text-green-600' : 'bg-yellow-100 text-yellow-600'}`}>{r.type}</span>
      <span className="text-sm font-sans text-red-500 line-through">{r.from_path}</span>
      <ArrowRight size={12} className="text-earth/30" />
      <span className="text-sm font-sans text-green-600 font-medium">{r.to_path}</span>
      <span className="text-[10px] text-earth/30 font-sans ml-auto">{r.hits || 0} hits</span>
      <button onClick={onDelete} className="text-red-400 hover:text-red-600" data-testid={`delete-redirect-${r.id}`}><Trash2 size={12} /></button>
    </div>
  );
}

export default function RedirectsTab({ redirects, setRedirects, draft, setDraft }) {
  const add = async () => {
    if (!draft.from_path || !draft.to_path) return;
    try {
      const { data } = await authAxios.post('/api/redirects', draft);
      setRedirects(prev => [data, ...prev]);
      setDraft(EMPTY_REDIRECT);
    } catch {
      alert('Failed');
    }
  };

  const remove = async (id) => {
    await authAxios.delete(`/api/redirects/${id}`);
    setRedirects(prev => prev.filter(r => r.id !== id));
  };

  return (
    <div data-testid="redirects-tab">
      <AddRedirectForm draft={draft} setDraft={setDraft} onAdd={add} />
      <div className="space-y-2">
        {redirects.length === 0 ? (
          <p className="text-center text-earth/30 font-sans text-sm py-8" data-testid="redirects-empty">No redirects configured</p>
        ) : redirects.map(r => <RedirectRow key={r.id} redirect={r} onDelete={() => remove(r.id)} />)}
      </div>
    </div>
  );
}
